import React from 'react';
import dayjs from 'dayjs';
import { Hit } from '@algolia/client-search';
import { BlockIcon, Card, RefreshIcon, Table } from '@avila-tek/ui/src';
import { createColumnHelper } from '@tanstack/react-table';
import {
  PageInfo,
  Plan,
  User,
  Company,
  Subscription,
  Pagination,
} from '../../models';
import { AlgoliaHitToObject } from '../../lib';
import { useAlgoliaSearch, useUser } from '../../hooks';
import AddSubscriptionModal from './AddSubscriptionModal/AddSubscriptionModal';

interface ActionCellProps {
  userId: string;
  renewSubscription: (userId: string) => void;
  blockSubscription: (userId: string) => void;
}

function ActionCell({
  userId,
  renewSubscription,
  blockSubscription,
}: ActionCellProps) {
  return (
    <div className="flex gap-5">
      <p className="flex gap-2 items-center">
        <RefreshIcon
          className="w-5 h-5 text-purple-300"
          onClick={(e) => {
            e.preventDefault();
            renewSubscription(userId);
          }}
        />
      </p>
      <p className="flex gap-2 items-center">
        <BlockIcon
          className="w-5 h-5 text-purple-300 "
          onClick={(e) => {
            e.preventDefault();
            blockSubscription(userId);
          }}
        />
      </p>
    </div>
  );
}

const columnHelper = createColumnHelper<User>();

interface SubscriptionsByPlanTableProps {
  planSelected: Partial<Plan>;
  plans: Plan[];
  data: Pagination<Partial<User>>;
  pagination: Partial<PageInfo>;
  setPagination: React.Dispatch<React.SetStateAction<Partial<PageInfo>>>;
}

function SubscriptionsByPlanTable({
  planSelected,
  plans,
  data,
  pagination,
  setPagination,
}: SubscriptionsByPlanTableProps) {
  const [user] = useUser();
  const [searchText, setSearchText] = React.useState<string | number>('');
  const [usersData, setUsersData] = React.useState([]);

  const [pageInfoAlgolia, setPageInfoAlgolia] = React.useState<PageInfo>({
    currentPage: 0,
    hasNextPage: false,
    hasPreviousPage: false,
    itemCount: 0,
    pageCount: 0,
    perPage: 10,
  });

  const { fetchData } = useAlgoliaSearch<User>({
    pageInfo: pageInfoAlgolia,
    setPageInfo: setPageInfoAlgolia,
    restrictSearchableAttributes: ['firstName', 'lastName', 'dni'],
    index: 'users',
    filters: `userType:client AND company._id:${
      (user.company as Company)?._id
    }  AND currentSubscription.plan:${planSelected?._id}   `,
  });

  React.useEffect(() => {
    async function syncData() {
      if (searchText) {
        const { pageInfo, items } = await fetchData({
          searchText: searchText.toString(),
          newPage: pageInfoAlgolia.currentPage,
          filters: `userType:client AND company._id:${
            (user.company as Company)?._id
          }  AND currentSubscription.plan:${planSelected?._id}   `,
        });
        setUsersData(AlgoliaHitToObject<User>(items as Hit<User>[]));
        setPageInfoAlgolia(pageInfo);
      } else {
        setUsersData(data?.items);
      }
    }
    syncData();
  }, [pageInfoAlgolia?.currentPage, searchText, planSelected?._id, pagination]);

  const handleGoToPage = (value: number) => {
    if (searchText) {
      setPageInfoAlgolia({ ...pageInfoAlgolia, currentPage: value - 1 });
    } else {
      setPagination({ ...pagination, currentPage: value });
    }
  };

  const [addSubscriptionModalOpen, setAddSubscriptionModalOpen] =
    React.useState(false);

  const onClickAddButton = () => {
    setAddSubscriptionModalOpen(true);
  };

  const columns = [
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore library has a issue: https://github.com/TanStack/table/issues/4224
    columnHelper.accessor('firstName', {
      header: 'Beneficiario',
      cell: (info) =>
        `${info.row.original.firstName} ${info.row.original.lastName}`,
    }),

    columnHelper.accessor('currentSubscription', {
      header: 'Duración',
      cell: (info) =>
        `${dayjs(
          (info.row.original.currentSubscription as Subscription)?.expiryDate
        ).diff(
          (info.row.original.currentSubscription as Subscription)?.startDate,
          'month'
        )} meses`,
    }),

    columnHelper.accessor('currentSubscription.startDate', {
      header: 'Inicio',
      cell: (info) =>
        `${dayjs(
          (info.row.original.currentSubscription as Subscription)?.startDate
        ).format('L')}`,
    }),

    columnHelper.accessor('currentSubscription.expiryDate', {
      header: 'Expiración',
      cell: (info) =>
        `${dayjs(
          (info.row.original.currentSubscription as Subscription)?.expiryDate
        ).format('L')}`,
    }),
  ];

  return (
    <>
      <Card className="overflow-x-auto p-5">
        <Table<User>
          data={usersData}
          columns={columns}
          manualPagination
          href="/app/subscription"
        >
          <Table.Header className="p-6 w-full flex justify-between">
            <h2 className="text-xl text-purple-300 font-semibold">
              {planSelected?.title}
            </h2>
            <Table.SearchInput
              manualFilter
              value={searchText.toString()}
              onChange={(value) => {
                setSearchText(value);
              }}
            />
            <Table.AddButton title="Adquirir" action={onClickAddButton} />
          </Table.Header>
          <Table.Content<User> />
          <Table.Footer>
            <Table.Pagination
              handleGoToPage={handleGoToPage}
              paginationInfo={{
                count: data.count,
                pageInfo: searchText
                  ? {
                      ...pageInfoAlgolia,
                      currentPage: pageInfoAlgolia.currentPage + 1,
                      perPage: data.pageInfo.perPage,
                    }
                  : data.pageInfo,
              }}
              className="ml-auto"
            />
          </Table.Footer>
        </Table>
      </Card>
      <AddSubscriptionModal
        isOpen={addSubscriptionModalOpen}
        setOpen={setAddSubscriptionModalOpen}
        plans={plans}
      />
    </>
  );
}

export default SubscriptionsByPlanTable;
